/**
 * Wechselkurse (PARITAET-1): Umrechnung mit dem letzten Kurs bis zu einem Tag, direkt, umgekehrt oder über den Euro (die EZB
 * veröffentlicht Referenzkurse nur als EUR → Währung), und die Frage, ob der letzte Kurs zu alt ist. Rein; dieselbe Regel wie
 * app.fx_rate in der Datenbank (0076).
 */
import type { IsoDate } from "./types.js";

export interface FxRateRow {
  base: string;
  quote: string;
  valid_on: string;
  rate: number | string;
}

/** Quelle der Referenzkurse (täglich gegen 16 Uhr MEZ, an TARGET-Geschäftstagen) */
export const ECB_DAILY_URL = "https://www.ecb.europa.eu/stats/eurofxref/eurofxref-daily.xml";

/** Letzter Kurs 1 from = x to bis einschließlich `day`; null ohne Kurs. */
export function fxRateAt(rates: readonly FxRateRow[], from: string, to: string, day: IsoDate): number | null {
  if (from === to) return 1;
  const pick = (b: string, q: string): number | null => {
    let best: FxRateRow | null = null;
    for (const r of rates) if (r.base === b && r.quote === q && r.valid_on <= day && (!best || r.valid_on > best.valid_on)) best = r;
    const v = best ? Number(best.rate) : NaN;
    return Number.isFinite(v) && v > 0 ? v : null;
  };
  const direct = pick(from, to);
  if (direct) return direct;
  const inverse = pick(to, from);
  if (inverse) return 1 / inverse;
  if (from !== "EUR" && to !== "EUR") {
    const eurTo = fxRateAt(rates, "EUR", to, day);
    const eurFrom = fxRateAt(rates, "EUR", from, day);
    if (eurTo && eurFrom) return eurTo / eurFrom;
  }
  return null;
}

/** Umrechnung mit dem letzten Kurs bis Monatsende (Übersicht, Kennzahlen, Creator-Portal); null ohne Kurs. */
export function fxMonthConverter(rates: readonly FxRateRow[]) {
  return (cents: number, from: string, to: string, month: string): number | null => {
    if (from === to) return cents;
    const rate = fxRateAt(rates, from, to, `${month.slice(0, 7)}-31`);
    return rate === null ? null : Math.round(cents * rate);
  };
}

/** Werktage (Montag bis Freitag) nach `from` bis einschließlich `to`. */
export function businessDaysBetween(from: IsoDate, to: IsoDate): number {
  if (to <= from) return 0;
  const d = new Date(`${from}T12:00:00Z`);
  const end = new Date(`${to}T12:00:00Z`);
  let n = 0;
  while (d < end) {
    d.setUTCDate(d.getUTCDate() + 1);
    const w = d.getUTCDay();
    if (w !== 0 && w !== 6) n += 1;
  }
  return n;
}

/**
 * Ist der letzte Kurs zu alt? Die EZB veröffentlicht an Werktagen; mehr als `maxBusinessDays` (Standard 3) Werktage ohne neuen
 * Kurs heißt, der tägliche Abruf läuft nicht. Ohne jeden Kurs immer veraltet.
 */
export function fxStaleness(lastValidOn: IsoDate | null, today: IsoDate, maxBusinessDays = 3): { stale: boolean; businessDays: number | null } {
  if (!lastValidOn) return { stale: true, businessDays: null };
  const businessDays = businessDaysBetween(lastValidOn.slice(0, 10), today);
  return { stale: businessDays > maxBusinessDays, businessDays };
}
